import React, {
  useEffect,
  useState,
} from 'react';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import PropTypes from 'prop-types';
import format from 'string-template';

import {
  HeaderContainer,
  SubTitle,
  Title,
  TitleContainer,
  Container,
} from '../../../../../../../components/v2/Card';
import InputLabel from '../../../../../../../components/v2/InputLabel';
import Package from '../../../../../../../Model/Package';
import Product from '../../../../../../../Model/Product';
import Table from '../../../../../../../components/v2/Table';
import useComponentMounted from '../../../../../../../hooks/useComponentMounted';
import { MONEY_DECIMALS, formatCurrencyCents } from '../../../../../../../utils/formatters';
import AddPackageModal from '../AddPackageModal';
import { StyledAddIcon } from '../AddPackageModal/styles';
import { PrimaryButton } from '../../../../../../../components/Button/ActionButtons';
import ConfirmationModal from '../../../../../../../components/v2/ConfirmationModal';
import LoadingOverlay from '../../../../../../components/LoadingOverlay';
import useToast from '../../../../../../hooks/useToast';
import useSessionStore from '../../../../../../../hooks/useSessionStore';
import useCanCreateChallengeProduct from '../../../../../../hooks/useCanCreateChallengeProduct';
import CollectionName from '../../../../../../../utils/collections';
import { CATEGORY_CONFIG } from '../../../../../../components/ContractModal/PackageCategoryCard/utils';
import { useCustomAppIconEnabled } from '../../../../../../../utils/postHog';
import { openFirestoreDocument } from '../../../../../../../utils/support';
import OnCallActions from '../../../../../../../components/OnCallActions';
import PackageMenu from '../ProductActionMenu/PackageMenu';
import { getPPFSelection, getPPFFieldsForSelection } from '../../postPaymentFormSelection';
import PostPaymentFormSelect from '../PostPaymentFormSelect';
import WelcomeVideoSection from '../WelcomeVideoSection';
import ProductAppIcon from './components/ProductAppIcon';
import {
  ArchivedChip,
  ButtonContainer,
  PackageNameCell,
  PPFControlRow,
  StyledArchiveIcon,
  StyledButton,
  StyledChip,
  ThreeDotButton,
  TitleRow,
  ProductCard,
} from './styles';
import texts from './texts.json';

const ProductView = ({
  product,
  showArchived,
  categorizedPackagesWithProduct,
  coachPPFForms,
  coachPPFFormsLoaded,
  coachDefaultFormId,
}) => {
  const [showAddPackageModal, setShowAddPackageModal] = useState(false);
  const [packageCollection, setPackageCollection] = useState({ docs: [] });
  const [isLoading, setIsLoading] = useState(true);
  const isComponentMountedRef = useComponentMounted();
  const [actionItem, setActionItem] = useState(null);
  const [menuState, setMenuState] = useState(null);
  const [pendingCategoryRemoval, setPendingCategoryRemoval] = useState(null);
  const { showToast } = useToast();
  const { isOnCallUser, isAdmin } = useSessionStore();
  // When the dedicated challenge-creation flow is available to this user, hide
  // the legacy convert action so challenge products are created there instead.
  const challengeCreationEnabled = useCanCreateChallengeProduct();
  // The product's own app icon and logo, rolled out through PostHog; admins only (see the product rules).
  const customAppIconEnabled = useCustomAppIconEnabled();

  useEffect(() => {
    const fetchPackages = async () => {
      setIsLoading(true);
      const packageCol = await product.getPackages();
      if (isComponentMountedRef.current) {
        setPackageCollection(packageCol);
        setIsLoading(false);
      }
    };
    fetchPackages();
  }, [isComponentMountedRef, product]);

  const onArchive = async () => {
    const isArchival = !actionItem.item.isArchived;
    setIsLoading(true);
    if (isArchival) {
      if (actionItem.isProduct) {
        const { productCategoryEntries = [] } = actionItem;
        await Promise.all([
          actionItem.item.archive(),
          ...productCategoryEntries.map(({ pkg }) => pkg.setCategory(null)),
        ]);
      } else {
        const { hasCategory: hasCategoryEntry } = actionItem;
        await actionItem.item.archive();
        if (hasCategoryEntry) {
          await actionItem.item.setCategory(null);
        }
      }
    } else {
      await actionItem.item.unarchive();
    }
    if (isComponentMountedRef.current) {
      setActionItem(null);
      setIsLoading(false);
    }
    showToast(format(isArchival ? texts.archiveSuccess : texts.unarchiveSuccess,
      { itemName: actionItem.isProduct ? texts.product : texts.package }));
  };

  const toggleChallengeProperties = (item) => {
    if (item.isChallengeProduct) {
      item.unsetAsChallengeProduct();
      return;
    }
    const productCategoryEntries = (categorizedPackagesWithProduct || [])
      .filter(({ pkg }) => pkg.productId === item.id);
    if (productCategoryEntries.length > 0) {
      setPendingCategoryRemoval({ type: 'challenge', item, productCategoryEntries });
      return;
    }
    item.setAsChallengeProduct();
  };

  const handleConfirmCategoryRemoval = async () => {
    setIsLoading(true);
    const { item, productCategoryEntries } = pendingCategoryRemoval;
    try {
      await Promise.all([
        item.setAsChallengeProduct(),
        ...productCategoryEntries.map(({ pkg }) => pkg.setCategory(null)),
      ]);
    } catch {
      showToast(texts.categoryUpdateError, { error: true });
    } finally {
      if (isComponentMountedRef.current) {
        setPendingCategoryRemoval(null);
        setIsLoading(false);
      }
    }
  };

  const handleMenuOpen = (event, pkg) => {
    event.stopPropagation();
    setMenuState({ anchorEl: event.currentTarget, pkg });
  };

  const handleMenuClose = () => {
    setMenuState(null);
  };

  const handleArchiveFromMenu = () => {
    const hasCategory = (categorizedPackagesWithProduct || []).some(({ pkg }) => pkg.id === menuState.pkg.id);
    setActionItem({ item: menuState.pkg, hasCategory });
    handleMenuClose();
  };

  // The dropdown writes the pin; the coach-default option clears it.
  const handlePPFSelectionChange = async (selection) => {
    try {
      await product.updateFields(getPPFFieldsForSelection(selection));
    } catch {
      showToast(texts.ppfUpdateError, { error: true });
    }
  };

  const getArchiveDescriptionTemplate = (item) => {
    if (item.item.isArchived) {
      return texts.unArchiveDesc;
    }
    if (item.productCategoryEntries?.length > 0) {
      return texts.archiveDescWithCategories;
    }
    if (item.hasCategory) {
      return texts.archivePackageDescWithCategory;
    }
    return texts.archiveDesc;
  };

  return (
    <ProductCard>
      {(!!actionItem) && (
        <ConfirmationModal
          open
          onClose={() => setActionItem(null)}
          onConfirm={onArchive}
          title={format(actionItem.item.isArchived ? texts.unarchiveItem : texts.archiveItem,
            { itemName: actionItem.isProduct ? texts.product : texts.package })}
          description={format(
            getArchiveDescriptionTemplate(actionItem),
            { itemName: actionItem.item.name },
          )}
        />
      )}
      {pendingCategoryRemoval && (
        <ConfirmationModal
          open
          onClose={() => setPendingCategoryRemoval(null)}
          onConfirm={handleConfirmCategoryRemoval}
          title={texts.categoryRemovalWarning.title}
          description={format(
            texts.categoryRemovalWarning.challengeDescription,
            { productName: pendingCategoryRemoval.item.name },
          )}
        />
      )}
      <HeaderContainer>
        <TitleContainer>
          <TitleRow>
            <Title>
              {product.name}
              {isAdmin && <ArchivedChip label={product.id} />}
              {product.isHyroxProduct && <StyledChip label={texts.hyrox} />}
              {product.isArchived && <ArchivedChip label={texts.archived} />}
              {product.isVideoFeedBackEnabled && <StyledChip label={texts.workoutFeedback} />}
              {product.isMealPlanEnabled && <StyledChip label={texts.mealPlan} />}
              {product.isCheckInFeedBackEnabled && <StyledChip label={texts.checkInFeedback} />}
              {product.isChatEnabled && <StyledChip label={texts.chat} />}
              {product.isAiFriendEnabled && <StyledChip label={texts.aiFriend} />}
            </Title>
            <ButtonContainer>
              <PrimaryButton
                onClick={() => {
                  const entries = (categorizedPackagesWithProduct || [])
                    .filter(({ pkg }) => pkg.productId === product.id);
                  setActionItem({ item: product, isProduct: true, productCategoryEntries: entries });
                }}
                icon={<StyledArchiveIcon />}
                variant="info"
                size="small"
              >
                {product.isArchived ? texts.unarchive : texts.archive}
              </PrimaryButton>
              {/* Legacy convert action. Hidden when the dedicated challenge-creation
                  flow is available (challenge products are created there instead).
                  Also hidden for Hyrox products — a challenge contract would assign
                  challenge programs on top of the auto-assigned Hyrox track — unless
                  one somehow already has both, so the unset action stays reachable. */}
              {!challengeCreationEnabled && (!product.isHyroxProduct || product.isChallengeProduct) && (
                <PrimaryButton
                  onClick={() => toggleChallengeProperties(product)}
                  variant="info"
                  size="small"
                >
                  {product.isChallengeProduct ? texts.unsetAsChallengeProduct : texts.setAsChallengeProduct}
                </PrimaryButton>
              )}
              <StyledButton onClick={() => setShowAddPackageModal(true)}>
                <StyledAddIcon />
                {texts.addPackage}
              </StyledButton>
            </ButtonContainer>
          </TitleRow>
          <SubTitle>{product.description}</SubTitle>
          <InputLabel>{texts.fieldNames.postPaymentForm}</InputLabel>
          <PPFControlRow>
            <PostPaymentFormSelect
              size="small"
              forms={coachPPFForms}
              formsLoaded={coachPPFFormsLoaded}
              value={getPPFSelection(product)}
              onChange={handlePPFSelectionChange}
              defaultFormId={coachDefaultFormId}
            />
          </PPFControlRow>
          <WelcomeVideoSection productId={product.id} coachId={product.coach} />
          {isAdmin && customAppIconEnabled && <ProductAppIcon product={product} />}
        </TitleContainer>
      </HeaderContainer>
      <Container>
        <Table
          enableSearch={false}
          rows={packageCollection.docs.slice().sort((a, b) => a.data.name.localeCompare(b.data.name))}
          filterFunction={(pkg) => showArchived || !pkg.isArchived}
          pageSize={4}
          loading={isLoading}
          columns={[
            {
              field: 'name',
              headerName: texts.headers.name,
              flex: 10,
              renderCell: ({ row: pkg }) => {
                const catConfig = pkg.category ? CATEGORY_CONFIG[pkg.category] : null;
                const { Tag: CategoryTag, label } = catConfig || {};
                return (
                  <PackageNameCell>
                    {pkg.name}
                    {CategoryTag && <CategoryTag>{format(texts.isCategoryLabel, { label })}</CategoryTag>}
                  </PackageNameCell>
                );
              },
            },
            {
              field: 'initialPaymentInCents',
              headerName: texts.headers.initial,
              flex: 5,
              renderCell: ({ row: { initialPaymentInCents, currency } }) => (
                formatCurrencyCents(
                  initialPaymentInCents,
                  currency,
                  MONEY_DECIMALS,
                )
              ),
            },
            {
              field: 'initialTerm',
              headerName: texts.headers.term,
              flex: 5,
              renderCell: ({ row: { initialTerm } }) => (
                initialTerm ? format(texts.monthText, { months: initialTerm }) : '-'),
            },
            {
              field: 'minSubscriptionMonths',
              headerName: texts.headers.commitment,
              flex: 5,
              renderCell: ({ row: { minSubscriptionMonths } }) => (
                minSubscriptionMonths ? format(texts.monthText, { months: minSubscriptionMonths }) : '-'
              ),
            },
            {
              field: 'monthlyPaymentInCents',
              headerName: texts.headers.monthly,
              flex: 5,
              renderCell: ({ row: { monthlyPaymentInCents, currency } }) => (
                formatCurrencyCents(
                  monthlyPaymentInCents,
                  currency,
                  MONEY_DECIMALS,
                )
              ),
            },
            {
              field: 'actions',
              headerName: texts.headers.actions,
              flex: 4,
              renderCell: ({ row: pkg }) => (
                <ThreeDotButton
                  size="small"
                  onClick={(e) => handleMenuOpen(e, pkg)}
                >
                  <MoreVertIcon fontSize="small" />
                </ThreeDotButton>
              ),
            },
          ]}
        />
      </Container>

      <PackageMenu
        menuState={menuState}
        onClose={handleMenuClose}
        onArchive={handleArchiveFromMenu}
      />

      {isOnCallUser && (
        <OnCallActions
          actions={[
            {
              onClick: () => openFirestoreDocument(`${CollectionName.PRODUCT}/${product.id}`),
              disabled: !product.id,
              label: texts.support.productDoc,
            },
          ]}
        />
      )}
      {showAddPackageModal && (
        <AddPackageModal
          open={showAddPackageModal}
          onClose={() => setShowAddPackageModal(false)}
          product={product}
        />
      )}
      <LoadingOverlay isLoading={isLoading} />
    </ProductCard>
  );
};

ProductView.propTypes = {
  product: PropTypes.instanceOf(Product).isRequired,
  showArchived: PropTypes.bool.isRequired,
  categorizedPackagesWithProduct: PropTypes.arrayOf(PropTypes.shape({
    pkg: PropTypes.instanceOf(Package),
    product: PropTypes.instanceOf(Product),
  })),
  coachPPFForms: PropTypes.array,
  coachPPFFormsLoaded: PropTypes.bool,
  coachDefaultFormId: PropTypes.string,
};

ProductView.defaultProps = {
  categorizedPackagesWithProduct: [],
  coachPPFForms: [],
  coachPPFFormsLoaded: undefined,
  coachDefaultFormId: '',
};

export default compose(
  observer,
)(ProductView);
